import React, { useMemo, useCallback, useEffect } from "react";
import type { Key, Dispatch, SetStateAction } from "react";
import { Tooltip, Tree } from "antd";
import type { DataNode } from "antd/es/tree";
import { useAppSelector } from "../../../..";
import { ReactComponent as PlusIcon } from "../../../../img/plus.svg";
import { Offer } from "../../../../lib/types";
import { IOfferHierarchy } from "../../../../store/slices/offering/types";
import { limitText, toDisplayFormat } from "../../../../lib/utils";
import { hasAccess } from "../../../RolesModule/hasAccess";
import { MENU_ADMIN_OFFERING_WRITE } from "../../../../lib/const/permissions";
import { OFFER_STATUSES } from "../../../../store/slices/offering/const";
import { getDatesSatisfyingHierarchyChildren } from "../utils/getDatesSatisfyingHierarchyChildren";
import "./GroupOfferTree.less";

interface ComponentProps {
  openAddOfferModal: () => void;
  addedOffer?: IOfferHierarchy | Offer;
  treeExpandedKeys: string[];
  setTreeExpandedKeys: Dispatch<SetStateAction<string[]>>;
  getTableRecordKey: (params: {
    parentId: number | string | undefined;
    id: number | undefined;
    fromDate: string | undefined;
    isAdded: boolean | undefined;
  }) => string;
  isVersionsOpen: boolean;
  activeOfferVersion: Offer<string, "view"> | undefined;
}

export const GroupOfferTree = (props: ComponentProps) => {
  const {
    openAddOfferModal,
    addedOffer,
    treeExpandedKeys,
    setTreeExpandedKeys,
    getTableRecordKey,
    isVersionsOpen,
    activeOfferVersion,
  } = props;

  const { offerHierarchy, offerHierarchyVersions } = useAppSelector((state) => state.offering);

  const getTreeDataItem = (
    hierarchy: Partial<IOfferHierarchy>,
    options: { isRoot?: boolean; parentId?: number | string }
  ): DataNode => {
    const { extName, id, children } = hierarchy;
    const fromDate = hierarchy.fromDate ? toDisplayFormat(hierarchy.fromDate) : undefined;
    const { isRoot, parentId } = options;
    const key = getTableRecordKey({
      parentId,
      id,
      fromDate,
      isAdded: hierarchy.isAdded,
    });
    const title = limitText(extName, 30);
    const datesSatisfyingChildren = getDatesSatisfyingHierarchyChildren({
      children,
      fromDate: activeOfferVersion?.fromDate,
      toDate: activeOfferVersion?.toDate,
    });
    return {
      title: <Tooltip title={extName}>{title}</Tooltip>,
      key,
      selectable: false,
      children: datesSatisfyingChildren?.length
        ? datesSatisfyingChildren.map((child) => getTreeDataItem(child, { parentId: key }))
        : undefined,
      className: isRoot ? "group-offer-tree__rootRow" : "group-offer-tree__row",
    };
  };

  const treeData = useMemo((): DataNode[] => {
    const currentHierarchy = isVersionsOpen ? offerHierarchyVersions : offerHierarchy;
    if (currentHierarchy) {
      return [
        getTreeDataItem(
          {
            ...currentHierarchy,
            children: addedOffer
              ? [...currentHierarchy.children, addedOffer]
              : currentHierarchy.children,
          },
          { isRoot: true }
        ),
      ];
    }
    if (activeOfferVersion) {
      return [
        getTreeDataItem(
          {
            extName: activeOfferVersion.extName,
            id: activeOfferVersion.id,
            children: addedOffer ? [addedOffer] : undefined,
            fromDate: activeOfferVersion.fromDate,
          },
          { isRoot: true }
        ),
      ];
    }
    return [];
  }, [offerHierarchy, addedOffer, activeOfferVersion, isVersionsOpen, offerHierarchyVersions]);

  // console.log({ treeData });

  const getAllExpandableKeys = useCallback((): string[] => {
    const keys: string[] = [];
    const collectKeys = ({ key, children }: DataNode) => {
      if (children?.length) {
        keys.push(String(key));
        children.forEach((child) => {
          collectKeys(child);
        });
      }
    };
    treeData.forEach((node) => {
      collectKeys(node);
    });
    return keys;
  }, [treeData]);

  // По открытии версий групповых предложений, должно разворачиваться все дерево
  // При наличии добавляемого предложения принудительное открытие первого уровня, если закрыт
  useEffect(() => {
    if (isVersionsOpen) {
      setTreeExpandedKeys(getAllExpandableKeys());
    } else if (treeData.length && addedOffer && treeExpandedKeys.length === 0) {
      const rootKey = treeData[0].key.toString().split("_")[0];
      setTreeExpandedKeys([rootKey]);
    }
  }, [isVersionsOpen, getAllExpandableKeys, treeData]);

  const titleRender = ({ title, className }: DataNode) => {
    const isChangeEnabledStatus =
      activeOfferVersion?.offeringStatusId === OFFER_STATUSES.PREPARE ||
      activeOfferVersion?.offeringStatusId === OFFER_STATUSES.SUSPENDED;

    return className === "group-offer-tree__rootRow" ? (
      <div>
        <h4>{title}</h4>
        {!addedOffer && isChangeEnabledStatus && hasAccess(MENU_ADMIN_OFFERING_WRITE) && (
          <Tooltip title="Добавить предложение">
            <PlusIcon className="group-offer-tree__addBtn" onClick={openAddOfferModal} />
          </Tooltip>
        )}
      </div>
    ) : (
      <div>{title}</div>
    );
  };

  // По дефолту в keys остаются активными вложенные элементы свернутого узла, удаление таковых
  const onExpand = (keys: Key[], { expanded, node }) => {
    if (expanded) {
      setTreeExpandedKeys(keys.map(String));
    } else {
      setTreeExpandedKeys((prevKeys) => {
        const activeKeys = prevKeys.filter((key) => !String(key).startsWith(String(node.key)));
        return activeKeys;
      });
    }
  };

  const showLine = useMemo(() => {
    if (offerHierarchy?.children && offerHierarchy?.children.length > 0) {
      return { showLeafIcon: false };
    }
    return false;
  }, [offerHierarchy]);

  return (
    <div className="group-offer-tree">
      <div className="group-offer-tree__title">Наименование</div>
      <Tree
        showLine={showLine}
        treeData={treeData}
        showIcon={false}
        onExpand={onExpand}
        expandedKeys={treeExpandedKeys}
        titleRender={titleRender}
      />
    </div>
  );
};
